import { FC, useMemo } from 'react';
import { Button } from 'antd';
import { TbCircleArrowUp, TbMist } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useSubjectsTags } from '@/queries';
import { VirtualizedList as List } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import CompanyResultGroupRows from '@/shared/components/company-search/CompanyResultGroupRows';
import { CompanySearchResultGroup, groupCompanySearchResults } from '@/shared/components/company-search/utils';
import { Tag, WorkspaceSubjectType } from '@/types';

import ListHeader from './components/01-ListHeader';
import CompanyListItem from './components/02-CompanyListItem';

interface ResultsListProps {
    groupRelatedListings: boolean;
    results: any;
    limit: any;
    page: number;
    resultsSelected: any;
    setResultsSelected: any;
    listColumns: any;
    onApplyColumnFilter: (column: string, result: unknown) => void;
    setListViewColumns: any;
    availableColumns: any;
    checkAll: any;
    setCheckAll: any;
    indeterminate: any;
    setIndeterminate: any;
    serverPaginated: boolean;
}

interface CompanyResultGroupItemProps {
    group: CompanySearchResultGroup;
    listColumns: any;
    onApplyColumnFilter: (column: string, result: unknown) => void;
    resultsCount: number;
    resultsSelected: any;
    setCheckAll: any;
    setIndeterminate: any;
    setResultsSelected: any;
    tagsBySubjectKey: Map<string, Tag[]>;
    tagsLoading: boolean;
}

const CompanyResultGroupItem: FC<CompanyResultGroupItemProps> = ({
    group,
    resultsCount,
    resultsSelected,
    setResultsSelected,
    listColumns,
    onApplyColumnFilter,
    setCheckAll,
    setIndeterminate,
    tagsBySubjectKey,
    tagsLoading,
}): JSX.Element => {
    const renderCompany = (result: any) => (
        <CompanyListItem
            key={result?.entityId ?? result?.symbol}
            data={result}
            resultsSelected={resultsSelected}
            setResultsSelected={setResultsSelected}
            listColumns={listColumns}
            onApplyColumnFilter={onApplyColumnFilter}
            setCheckAll={setCheckAll}
            setIndeterminate={setIndeterminate}
            resultsCount={resultsCount}
            tags={tagsBySubjectKey.get(
                `${result?.resultType === 'importedEntity' ? WorkspaceSubjectType.IMPORTED_ENTITY : WorkspaceSubjectType.SEARCH_COMPANY}:${result?.entityId ?? result?.symbol}`,
            )}
            tagsLoading={tagsLoading}
        />
    );

    return <CompanyResultGroupRows group={group} renderResult={renderCompany} />;
};

const ResultsList: FC<ResultsListProps> = ({
    groupRelatedListings,
    results,
    limit,
    page,
    resultsSelected,
    setResultsSelected,
    availableColumns,
    listColumns,
    onApplyColumnFilter,
    setListViewColumns,
    checkAll,
    indeterminate,
    setCheckAll,
    setIndeterminate,
    serverPaginated,
}): JSX.Element => {
    const { planTier } = useEntitlements();
    const { token } = useAntDesignTheme();

    const navigate = useNavigate();

    // Check/Uncheck / Indeterminate Checkboxes logic

    const handleCheckAllChange = (checked: boolean) => {
        if (checked) {
            const allItems = new Set(results.map((item: any) => item.entityId ?? item.symbol)); // Add all item identifiers

            setResultsSelected(allItems);
        } else {
            setResultsSelected(new Set()); // Clear all selections
        }

        setCheckAll(checked);
        setIndeterminate(false); // Not indeterminate when "Select All" is checked or unchecked
    };

    const effectivePage = Math.min(page, Math.max(1, Math.ceil(results.length / Math.max(1, limit))));
    const visibleResults = useMemo(
        () => (serverPaginated ? results : results?.slice((effectivePage - 1) * limit, effectivePage * limit)),
        [effectivePage, limit, results, serverPaginated],
    );
    const groupedVisibleResults = useMemo(
        () =>
            groupRelatedListings
                ? groupCompanySearchResults(visibleResults)
                : visibleResults.map((result: any) => ({
                      alternatives: [],
                      id: String(result?.entityId ?? result?.symbol ?? result?.id ?? result?.name),
                      label: result?.companyName ?? result?.name ?? result?.symbol ?? 'Company',
                      primary: result,
                  })),
        [groupRelatedListings, visibleResults],
    );
    const visibleSubjects = useMemo(
        () =>
            visibleResults.map((result: any) => ({
                subjectId: String(result?.entityId ?? result?.symbol),
                subjectType:
                    result?.resultType === 'importedEntity'
                        ? WorkspaceSubjectType.IMPORTED_ENTITY
                        : WorkspaceSubjectType.SEARCH_COMPANY,
            })),
        [visibleResults],
    );
    const resultTagsQuery = useSubjectsTags(visibleSubjects);
    const tagsBySubjectKey = new Map(
        (resultTagsQuery.data ?? []).map((subject) => [`${subject.subjectType}:${subject.subjectId}`, subject.tags]),
    );

    return (
        <>
            {planTier === 'free' && (
                <PlatformAlert
                    showIcon
                    icon={<TbMist style={{ marginTop: '0.25rem' }} />}
                    status='info'
                    message='Search results limited by current plan.'
                    description='For international assets, advanced data enrichment, and more - upgrade your plan today!'
                    style={{
                        marginInline: '0.5rem',
                        marginTop: '0.5rem',
                    }}
                    action={
                        <Button
                            type='primary'
                            onClick={() => {
                                navigate(`/settings/account/plans`);
                            }}
                        >
                            <TbCircleArrowUp
                                size='1rem'
                                style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }}
                            />
                            Upgrade Plan
                        </Button>
                    }
                />
            )}
            {visibleResults?.length !== 0 && (
                <List
                    itemLayout='vertical'
                    size='small'
                    style={{
                        paddingInline: 0,
                    }}
                    headerStyle={{
                        background: token.colorBgContainer,
                        borderBottom: `1px solid ${token.colorBorder}`,
                        boxSizing: 'border-box',
                        paddingBlock: '0.5rem',
                        paddingInline: '0.5rem',
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                    }}
                    header={
                        <ListHeader
                            availableColumns={availableColumns}
                            listColumns={listColumns}
                            setListViewColumns={setListViewColumns}
                            results={visibleResults}
                            resultsSelected={resultsSelected}
                            setResultsSelected={setResultsSelected}
                            handleCheckAllChange={handleCheckAllChange}
                            checkAll={checkAll}
                            indeterminate={indeterminate}
                        />
                    }
                    dataSource={groupedVisibleResults}
                    renderItem={(group: CompanySearchResultGroup) => (
                        <CompanyResultGroupItem
                            key={group.id}
                            group={group}
                            onApplyColumnFilter={onApplyColumnFilter}
                            resultsSelected={resultsSelected}
                            setResultsSelected={setResultsSelected}
                            listColumns={listColumns}
                            setCheckAll={setCheckAll}
                            setIndeterminate={setIndeterminate}
                            resultsCount={visibleResults.length}
                            tagsBySubjectKey={tagsBySubjectKey}
                            tagsLoading={resultTagsQuery.isLoading}
                        />
                    )}
                />
            )}
        </>
    );
};

export default ResultsList;
